import { messages } from './../../mock-api/common/messages/data';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    catchError,
    map,
    Observable,
    tap,
    throwError,
} from 'rxjs';
import { EditWithdrawTransaction } from './transactions.types';
import { environment } from '../../../environments/environment';
import { WithdrawAction } from '../store/user/models/action.model';

@Injectable({
    providedIn: 'root',
})
export class TransactionsService {
    private _data: BehaviorSubject<any> = new BehaviorSubject(null);
    private api: string = environment.api;

    constructor(private _httpClient: HttpClient) {}

    get data$(): Observable<any> {
        return this._data.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Take last 5 transactions
    // --

    // getLastFiveTransactions() {
    //     return this._httpClient
    //         .get(`/api/transactions/list?take=5&skip=0`)
    //         .pipe(
    //             catchError((error: any): any => {
    //                 throw new Error(error.message);
    //             })
    //         );
    // }

    getLastFiveTransactions(take?: number): Observable<any> {
        return this._httpClient
            .get(`${this.api}/transactions/list?take=${take}&skip=0`)
            .pipe(
                map((response: any) => {
                    let result: any = [];

                    result = this.mapResult(response?.data?.actions);
                    return { result, count: response?.data?.count };
                }),
                tap((response: any) => {
                    this._data.next(response);
                })
            );
    }

    getAllTransactions(take?: number, skip?: number): Observable<any> {
        return this._httpClient
            .get(`${this.api}/transactions/list?take=${take}&skip=${skip}`)
            .pipe(
                map((response: any) => {
                    // console.log(response);
                    let result: any = [];
                    result = this.mapResult(response?.data?.actions);
                    return {
                        result,
                        count: response?.data?.count,
                    };
                })
            );
    }

    getAllResolutions(
        take?: number,
        skip?: number,
        paginationCursor?: number
    ): Observable<any> {
        return this._httpClient
            .get(
                `${this.api}/transactions/resolutions?take=${take}&skip=${skip}&cursor=${paginationCursor}`
            )
            .pipe(
                map((response: any) => {
                    const result: any = [];
                    // result = this.mapResult(response?.resolution?.resolutions);
                    return {
                        // result,
                        result: response?.data?.resolutions,
                        count: response?.data?.count,
                        paginationCursor: response?.data?.paginationCursor,
                        totalAmount: parseFloat(
                            (response?.data?.totalAmount ?? 0).toFixed(2)
                        ),
                    };
                })
            );
    }

    getTransaction(id: string) {
        return this._httpClient.get(`${this.api}/transactions/${id}`).pipe(
            map((response: any) => {
                let result: any = [];
                result = this.mapResult(response?.transactionsList);
                return { result };
            })
        );
    }

    getTransactionPaginate(take: number, skip: number): any {
        return new Promise((resolve, reject) =>
            this._httpClient
                .get(`${this.api}/transactions/list?take=${take}&skip=${skip}`)
                .pipe(
                    catchError((error: any): any => {
                        reject([]);
                        throw new Error(error.message);
                    }),
                    map((response: any) => {
                        let result: any = [];

                        result = this.mapResult(response?.data);
                        return result;
                    })
                )
                .subscribe((response) => {
                    resolve(response);
                })
        );
    }

    checkForNumbers(str: any) {
        if (typeof str === 'number') {
            return;
        }
        if (!str) {
            return false;
        }
        const data = str.replace(/[0-9]/g, '');
        if (!data) {
            return true;
        }
        return false;
    }

    getFilteredTransactions(body: any, take: number, skip: number): any {
        body.take = take;
        body.skip = skip;
        if (this.checkForNumbers(body.depositId)) {
            body.depositId = Number(body.depositId);
        }

        return this._httpClient
            .post(`${this.api}/transactions/list`, body)
            .pipe(
                catchError((error: any): any => {
                    throw new Error(error.message);
                }),
                map((response: any) => ({
                    result: response?.data?.actions,
                    count: response?.data?.count,
                    totalAmount: parseFloat(
                        (response?.data?.totalAmount ?? 0).toFixed(2)
                    ),
                }))
            );
    }

    getFilteredTransactionsV1(body: any): any {
        if (this.checkForNumbers(body.depositId)) {
            body.depositId = Number(body.depositId);
        }

        return this._httpClient
            .post(`${this.api}/transactions/list-v1`, body)
            .pipe(
                catchError((error: any): any => {
                    throw new Error(error.message);
                }),
                map((response: any) => {
                    let result: any = [];

                    result = this.mapResult(response?.data?.actions);
                    return result;
                })
            );
    }

    closeTransaction(dto: any) {
        return this._httpClient
            .patch(`${this.api}/transactions/close-transaction`, dto)
            .pipe(
                catchError((error) => {
                    throw new Error(error.error.message);
                })
            );
    }

    getTransactionByCCMask(body: { first: string; last: string }) {
        return this._httpClient
            .get(
                `${this.api}/transactions/get-all-successful-transaction-by-ccmask?first=${body.first}&last=${body.last}`
            )
            .pipe(
                catchError((error) => {
                    throw new Error(error.error.message);
                })
            );
    }

    getTransactionAmountRates(tid: string) {
        return this._httpClient
            .get(`${this.api}/transactions/transaction-amount-rates/${tid}`)
            .pipe(
                catchError((error) => {
                    throw new Error(error.error.message);
                })
            );
    }

    //transaction-amount-rates
    getSupportStatus(resolutionId: number) {
        return this._httpClient
            .post(`${this.api}/transactions/support-status`, { resolutionId })
            .pipe(
                catchError((error) => {
                    throw new Error(error.error.message);
                })
            );
    }

    updateSupportStatus(body: any) {
        return this._httpClient
            .post(`${this.api}/transactions/update-support-status`, body)
            .pipe(
                catchError((error) => {
                    throw new Error(error.error.message);
                })
            );
    }

    private mapResult(array: Array<any>): Array<any> {
        const result = [];

        array.forEach((element) => {
            result.push({
                ...element,
                depositId: element?.metadata?.depositId,
                merchant: element?.metadata?.merchant,
                userid: element?.metadata?.userid,
                amount: element?.metadata?.amount,
                currency: element?.metadata?.currency,
                paymentSystem: element?.metadata?.paymentSystem,
                failReason: element?.metadata?.failReason,
                ccMask: element?.metadata?.ccMask,
                cryptoAmount: element?.metadata?.paidAmountCrypto,
                subject: [],
            });
        });

        return result;
    }

    checkIfTransactionExpired(tid: string) {
        return this._httpClient
            .get(`${this.api}/is-transaction-expired/${tid}`)
            .pipe(
                catchError((error) => {
                    throw new Error(error.error.message);
                })
            );
    }

    checkIfTransactionExist(tid: string) {
        return this._httpClient
            .post(`${this.api}/is-transaction-exist`, { transactionId: tid })
            .pipe(
                catchError((error) => {
                    throw new Error(error.error.message);
                })
            );
    }

    updateTransactionStatus(body: {
        transactionId: string;
        userId: string;
        executionId?: string;
        status: string;
        browserInfo?: string;
    }) {
        return this._httpClient
            .post(`${this.api}/update-transaction`, body)
            .pipe(
                catchError((error) => {
                    console.log(error, 'error');
                    throw new Error(error.error.message);
                })
            );
    }

    getIframes(transactionCharge) {
        try {
            const url = `${this.api}/get-iframes?${transactionCharge}`;
            const url_localhost = `http://localhost:3050/get-iframes?${transactionCharge}`;
            return this._httpClient.get(url).pipe(
                catchError((error) => {
                    throw new Error(error.error.message);
                })
            );
        } catch (error) {
            return error;
        }
    }

    createPaytentlyTransaction(transactionCharge) {
        try {
            const url = `${this.api}/create-paytently-transaction?${transactionCharge}`;
            const url_localhost = `http://localhost:3050/create-paytently-transaction?${transactionCharge}`;
            return this._httpClient.get(url).pipe(
                catchError((error) => {
                    throw new Error(error.error.message);
                })
            );
        } catch (error) {
            return error;
        }
    }

    editWithdrawTransaction(
        body: EditWithdrawTransaction
    ): Observable<WithdrawAction> {
        const url: string = environment.production
            ? `${this.api}/edit-withdraw-transaction`
            : 'http://localhost:3050/edit-withdraw-transaction';
        return this._httpClient
            .post<WithdrawAction>(url, body, { withCredentials: true })
            .pipe(
                catchError((error) => {
                    console.log(error, 'error');
                    throw new Error(error.error.message);
                })
            );
    }
}
